import { ReactNode } from "react";
import { marked } from "marked";
import DOMPurify from "dompurify";

// Configure marked once for chat-friendly output
marked.setOptions({
  gfm: true,
  breaks: true,
});

// A link in a message (from the user or the agent) must open in a new browser tab, never inside
// the chat window/iframe itself — the default renderer emits a plain <a href="...">, which
// hijacks the chat (most visibly, navigates the embedded widget's own iframe away entirely).
// Mirrors marked's own default `link` renderer (href encoding, optional title) and just adds
// target/rel; rel="noopener noreferrer" matters specifically because this widget runs inside a
// cross-origin iframe — without it, the new tab would get a live `window.opener` handle back in.
marked.use({
  renderer: {
    link({ href, title, tokens }) {
      const text = this.parser.parseInline(tokens);
      let safeHref: string;
      try {
        safeHref = encodeURI(href).replace(/%25/g, "%");
      } catch {
        return text;
      }
      const titleAttr = title
        ? ` title="${String(title).replace(/&/g, "&amp;").replace(/"/g, "&quot;")}"`
        : "";
      return `<a href="${safeHref}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
    },
  },
});

export function renderChatMarkdown(mdText: string): ReactNode[] {
  if (!mdText) {
    return [];
  }

  const rawHtml = marked.parse(mdText) as string;
  // DOMPurify strips `target` by default (it's not in its allowed-attribute list, unlike `rel`,
  // specifically to stop untrusted content from doing this) — ADD_ATTR explicitly allows it back
  // through so the link renderer's target="_blank" above actually survives sanitization.
  const safeHtml = DOMPurify.sanitize(rawHtml, { ADD_ATTR: ["target"] });

  return [
    <div
      key="markdown"
      style={{ marginBottom: 4 }}
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />,
  ];
}
